import React, { useEffect, useMemo, useState } from "react";
import { CheckBox, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import { getShortTitle, ProductAttributeObject } from "./types";
import { setProductType } from "@redux/slices/appStateSlice";
import { useSelector } from "react-redux";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import {
  getRequestType,
  isListedPriceEqualsOfferPrice,
  RequestTypes,
  showRequestItemFromSupplier,
} from "@scenes/requests/utils/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { ProductCategory } from "@utils/constants";
import { Pressable, TouchableOpacity } from "react-native";
import RenderIcon from "./IconView";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import useFilteredSections from "../../../hooks/usePriceModeUpdate";
import { PriceModes, ViewType } from "../PriceModeCard/types";
import {
  getProductTypeFromRequest,
  renderRequestStatus,
} from "@scenes/requests/utils/RequestsScreen";
import { isMeleeItem } from "@utils/product/productValidators";
import ExtraDetailsContainer from "@components/molecules/RequestViewCard/ExtraDetails";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useBulkRequestState } from "@scenes/requests/context/BulkStateContext";
import { View } from "moti";
import PriceContainer from "@components/molecules/SearchResultListItem/PriceContainer";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { useItemDetailsCardSubSections } from "../../../hooks/useSortCustomData";
import { CustomisationKey } from "@scenes/pdp/types";
import { setSectionName } from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import SecondSectionWithFilteredData from "../SearchResultListItem/SecondSectionWithFilteredData";
import ImageComponentWithSummary from "../SearchResultListItem/ImageComponentWithSummary";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { useAvailabilityData } from "../../../hooks/useAvailabilityData";
import { usePdpLayout } from "@redux/slices/pdpLayoutSlice";
import SecondSectionWithFilteredPairData from "../SearchResultListItem/SecondSectionWithFilteredPairData";
import { StoneType } from "@scenes/bulk-request/Utils";
import BulkQuantityEditor from "../BulkQuantityEditor";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import {
  handleSupplierForPaUser,
  isLinkedPair,
  isPair,
  isParcel,
} from "@utils/product/productValidators";
import {
  formatNumberWithPrecision,
  handleDeepLink,
  percent,
} from "@utils/product/productFormatters";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import { MemoStatus } from "@src/types/HistoryRequest";
import {
  useAllowQuantityOnBulk,
  useEnableRequestItemFromSupplier,
} from "@redux/slices/globalSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import QuantitySelector from "../QuantitySelector";
import { css } from "styled-components/native";
import ItemDetails from "@components/molecules/OrderCartItemCard/ItemDetails";
import PersonalisationSection from "@components/molecules/OrderCartItemCard/PersonalisationSection";

const hasEntries = (value?: Record<string, unknown> | null) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

const isDisplayableValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const formatConfigKey = (key: string) => {
  if (key.includes(" ") || /[A-Z]/.test(key)) return key;
  return key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const visibleConfigurationEntries = (
  source?: Record<string, unknown> | null
) => {
  if (!hasEntries(source)) return {};
  return Object.entries(source as Record<string, unknown>).reduce(
    (result, [key, value]) => {
      if (key === "Total Price" || !isDisplayableValue(value)) return result;
      result[formatConfigKey(key)] = value;
      return result;
    },
    {} as Record<string, unknown>
  );
};

const RequestViewCard = (props: any) => {
  const {
    stockItemRequest,
    productType,
    validValue,
    isSelected,
    onSelect,
    showCheckbox,
    hasChildren = false,
    onEdit,
    showListPrice,
    isTotalOfferDiscountApplied,
    fromRequestDetailScreen,
    hasAnyOfferDetails,
    skipUnread,
    isModifyScreen,
    saveBulkItemDetails,
  } = props;
  const theme = useTheme();
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const { isVDBOrg } = useOrgSettingsSlice();
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const productPrefix = useProductPrefix(productType.productType);
  const { deviceWidth } = useIsFoldable();
  const [requestType, setRequestType] = useState<RequestTypes>(
    RequestTypes.Buy
  );

  const isListedPriceDisplay =
    fromRequestDetailScreen &&
    ((requestType === RequestTypes.Quote &&
      stockItemRequest.view_type === ViewType.SellerView &&
      stockItemRequest?.parent_id !== 0) ||
      (stockItemRequest?.children && requestType !== RequestTypes.Quote))
      ? fromRequestDetailScreen && isTotalOfferDiscountApplied
      : true;

  const isForceHide =
    stockItemRequest?.view_type !== ViewType.BuyerView &&
    skipUnread &&
    stockItemRequest?.quote_details?.total_offer_price_applied;

  const isTotalOfferPrice =
    isCustomApp && requestType === RequestTypes.Quote
      ? isForceHide
        ? true
        : fromRequestDetailScreen &&
          !showListPrice &&
          !hasAnyOfferDetails &&
          stockItemRequest?.view_type !== ViewType.BuyerView
      : false;

  const [isListedEqualsOffer, setIsListedEqualsOffer] = useState(false);
  const { items } = useBulkRequestState();
  const dispatch = useDispatch();
  const matchingItem =
    items &&
    items?.find((item) => item?.stock_item_request_id === stockItemRequest?.id);
  const commentToDisplay = matchingItem
    ? matchingItem.comments
    : stockItemRequest?.comments;
  const notesToDisplay = matchingItem
    ? matchingItem?.notes
    : stockItemRequest?.notes;

  const stockItem = useMemo(() => {
    const baseItem = stockItemRequest[productType.productType] ?? {};

    return {
      ...baseItem,
      ...(stockItemRequest?.quantity
        ? { quantity: stockItemRequest.quantity }
        : {}),
      ...(stockItemRequest?.offer_size
        ? { offer_size: stockItemRequest.offer_size }
        : {}),
      ...(!baseItem?.updated_discount_percent &&
      stockItemRequest?.offer_discount_percent?.toString() !==
        baseItem?.discount_percent?.toString()
        ? { updated_discount_percent: stockItemRequest?.offer_discount_percent }
        : {}),
      ...(!baseItem?.updated_price_per_carat &&
      stockItemRequest?.offer_price_per_carat?.toString() !==
        baseItem?.price_per_carat?.toString()
        ? { updated_price_per_carat: stockItemRequest?.offer_price_per_carat }
        : {}),
      ...(!baseItem?.updated_total_sales_price &&
      stockItemRequest?.offer_total_sales_price?.toString() !==
        baseItem?.total_sales_price?.toString()
        ? {
            updated_total_sales_price:
              stockItemRequest?.offer_total_sales_price,
          }
        : {}),
    };
  }, [stockItemRequest, productType]);

  const shortTitle = useMemo(
    () => getShortTitle(stockItem, productType.productType),
    [stockItem, productType]
  );

  const cellWidth =
    deviceWidth / (stockItemRequest.offer_total_sales_price ? 4.7 : 3.7);
  const keyCellWidth = cellWidth - 30;

  const stoneItem = stockItemRequest[productType.productType] ?? {};
  let mStoneType = SearchFilterTypeEnum.SINGLE_STONES;
  if (Object.keys(stoneItem).length > 0) {
    mStoneType = isPair(stoneItem)
      ? SearchFilterTypeEnum.PAIRS
      : isCustomApp && isParcel(stoneItem)
      ? SearchFilterTypeEnum.PARCEL
      : SearchFilterTypeEnum.SINGLE_STONES;
  }

  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType.productType,
    mStoneType
  );

  const sectionDetailLayout = useGetPdpSectionLayout(
    PdpSections.PRODUCT_DETAILS,
    productType.productType,
    mStoneType
  );

  const filteredData = useFilteredSections(
    sectionLayout.sub_section,
    userInfo?.user?.price_mode
  );

  const filteredDetailData = useFilteredSections(
    sectionDetailLayout?.options,
    userInfo?.user?.price_mode
  );

  const getFilterData = (searchText: string): boolean => {
    const filteredArray = filteredData?.filter(
      (item) => item.name === searchText
    );
    return filteredArray?.length !== 0;
  };

  const getFilterDetailData = (searchText: string): boolean => {
    const filteredArray = filteredDetailData?.filter(
      (item) => item.name === searchText
    );
    return filteredArray?.length !== 0;
  };

  const Wrapper = showListPrice === false ? Pressable : View;

  const visibleConfig = useMemo(() => {
    const configurationDetails =
      stockItemRequest?.configuration_details?.config;
    const configSource = hasEntries(configurationDetails)
      ? configurationDetails
      : stockItemRequest?.additional_attributes?.config;
    return visibleConfigurationEntries(configSource);
  }, [stockItemRequest]);
  const visiblePersonalization = useMemo(
    () =>
      visibleConfigurationEntries(
        hasEntries(stockItemRequest?.configuration_details?.personalization)
          ? stockItemRequest?.configuration_details?.personalization
          : stockItemRequest?.additional_attributes?.personalization
      ),
    [stockItemRequest]
  );
  const hasVisibleConfig = Object.keys(visibleConfig).length > 0;
  const hasVisiblePersonalization =
    Object.keys(visiblePersonalization).length > 0;

  const showTitle = getFilterData(ProductAttributeObject.sub_title);
  const showStockNumber = getFilterData(ProductAttributeObject.stock_no);
  const showCut = getFilterDetailData(ProductAttributeObject.cut);
  const showPolish = getFilterDetailData(ProductAttributeObject.polish);
  const showSymmetry = getFilterDetailData(ProductAttributeObject.symmetry);
  const showfluor = getFilterDetailData(ProductAttributeObject.fluor_intensity);

  useEffect(() => {
    const type = getRequestType(stockItemRequest.request_type);
    const isLEqualsO = isListedPriceEqualsOfferPrice(
      stockItemRequest,
      productType.productType
    );
    setRequestType(type);
    if (
      fromRequestDetailScreen === true &&
      ((stockItemRequest?.parent_id !== 0 &&
        requestType === RequestTypes.Quote &&
        stockItemRequest.view_type === ViewType.SellerView) ||
        (stockItemRequest?.children && requestType !== RequestTypes.Quote))
    ) {
      setIsListedEqualsOffer(false);
    } else {
      setIsListedEqualsOffer(isLEqualsO);
    }
  }, [stockItemRequest]);

  const isPurchasingAgent =
    stockItemRequest &&
    stockItemRequest?.bmh_request_type === "purchasing_agent";

  const stoneType = isPair(stockItem)
    ? StoneType.PAIR
    : isCustomApp && isParcel(stockItem)
    ? StoneType.PARCELS
    : StoneType.SINGLE_STONES;

  const sectionName =
    stoneType === StoneType.PAIR
      ? SearchFilterTypeEnum.PAIRS
      : isCustomApp && stoneType === StoneType.PARCELS
      ? SearchFilterTypeEnum.PARCEL
      : SearchFilterTypeEnum.SINGLE_STONES;

  const { priceDetailsDataOptions } =
    useItemDetailsCardSubSections(
      productType.productType as ProductCategory,
      userInfo.getPriceMode(),
      "",
      true,
      stoneType,
      true
    ) || {};
  const pdpLayout = usePdpLayout();
  const showAvailabilityDot = useAvailabilityData(
    pdpLayout?.[productType?.productType ?? ""]?.availabilty_price_modes ?? [
      PriceModes.GREEN,
      PriceModes.ORANGE,
      PriceModes.WHITE,
    ]
  );

  const filteredPriceDetailsDataOptions = priceDetailsDataOptions?.filter(
    (menuItem: any) => {
      if (
        stockItem?.diamond?.is_fancy &&
        menuItem?.name === CustomisationKey.discount_percent
      ) {
        return false;
      }
      return true;
    }
  );

  return (
    <>
      {stockItem && stockItem.id ? (
        <CardContainer
          onPress={() => {
            if (
              (stockItemRequest?.memo_status === MemoStatus.Sold ||
                stockItemRequest?.memo_status === MemoStatus.Return) &&
              stockItemRequest?.view_type === ViewType.BuyerView
            ) {
              return;
            }
            const productSearchType = getProductTypeFromRequest(
              productType?.productType
            );
            if (theme.isLargeScreen) {
              dispatch(setProductType(productSearchType));
              dispatch(setSectionName(getSectionName(productSearchType)));
            }
            const requestAttributes =
              stockItemRequest.additional_attributes ?? {};
            const hasRequestConfiguration =
              hasEntries(requestAttributes.config) ||
              hasEntries(requestAttributes.personalization);
            navigate(Routes.GlobalNavigator, {
              screen: Routes.PDPScreen,
              params: {
                productID: stockItem.id,
                productType: stockItem.type,
                fromRequestDetailsScreen: true,
                ...(hasRequestConfiguration && {
                  additionalAttributes: JSON.stringify(requestAttributes),
                }),
                productPrefix,
              },
            });
          }}
        >
          {stoneType === StoneType.PAIR || stoneType === StoneType.PARCELS ? (
            <FirstContainer>
              <ImageComponentWithSummary
                mItem={stockItem}
                isCheckboxVisible={false}
                productType={productType?.productType}
                showAvailabilityDot={showAvailabilityDot}
                fromBulkList={false}
                sectionName={sectionName}
                viewType={stockItemRequest.view_type}
                requestType={getRequestType(requestType)}
                hasAnyOfferDetails={hasAnyOfferDetails}
                fromRequestModify={false}
                stoneType={stoneType}
              />
              {stockItemRequest && (
                <PairStatusContainer>
                  {
                    <VDBText
                      type={typography["Minor-R"]}
                      color={
                        theme[
                          renderRequestStatus(matchingItem?.status, t)
                            .colorCode as keyof DefaultTheme
                        ] as string
                      }
                    >
                      {renderRequestStatus(matchingItem?.status, t).status}
                    </VDBText>
                  }
                </PairStatusContainer>
              )}
            </FirstContainer>
          ) : (
            <TopContainer>
              {showCheckbox && (
                <CheckboxContainer>
                  <Wrapper
                    onPress={
                      showListPrice ||
                      String(stockItemRequest?.status ?? "").toLowerCase() !==
                        "pending"
                        ? undefined
                        : checkStateChanged
                    }
                  >
                    <CheckBox
                      fillColor={theme["knob-bor"]}
                      checkBoxSize={20}
                      checkedFunction={() => onSelect(stockItemRequest.id)}
                      isChecked={isSelected}
                      disabled={
                        String(stockItemRequest?.status ?? "").toLowerCase() !==
                        "pending"
                      }
                    />
                  </Wrapper>
                </CheckboxContainer>
              )}
              <RenderIcon
                image_url={stockItem?.image_url}
                icon={stockItem?.shape}
                isPair={isPair(stockItemRequest) ?? false}
              />
              <Spacer size={SpaceType.Basic} />
              <FlexContainer>
                {showTitle && (
                  <>
                    <TopWrapper>
                      <VDBText
                        type={typography["Minor-M"]}
                        color={theme["core-05"]}
                      >
                        {shortTitle}
                      </VDBText>
                    </TopWrapper>
                    <Spacer size={SpaceType.Small} />
                  </>
                )}
                <IconContainer>
                  {showCheckbox && stockItemRequest?.status === "pending" && (
                    <TouchableOpacity
                      onPress={() => onEdit?.(stockItemRequest)}
                    >
                      <VDBIcon
                        name={VDBIcons.EDIT}
                        size={24}
                        color={theme["core-06"]}
                        variant={IconsVariant.VDBCustom}
                      />
                    </TouchableOpacity>
                  )}
                </IconContainer>
                {[
                  ProductCategory.DIAMOND,
                  ProductCategory.LAB_GROWN_DIAMOND,
                ].includes(productType.productType) && (
                  <CPSContainer>
                    {showCut && (
                      <>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {t("requestHistoryDetail.cut.text")}:
                        </VDBText>
                        <Spacer size={SpaceType.Tiny} />
                        <VDBText
                          type={typography["Minor-M"]}
                          color={theme["core-05"]}
                        >
                          {stockItem.cut_short}
                        </VDBText>
                        <Spacer size={SpaceType.Small} />
                      </>
                    )}

                    {showPolish && (
                      <>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {t("requestHistoryDetail.polish.text")}:
                        </VDBText>
                        <Spacer size={SpaceType.Tiny} />
                        <VDBText
                          type={typography["Minor-M"]}
                          color={theme["core-05"]}
                        >
                          {stockItem.polish_short}
                        </VDBText>
                        <Spacer size={SpaceType.Small} />
                      </>
                    )}

                    {showSymmetry && (
                      <>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {t("requestHistoryDetail.symmetry.text")}:
                        </VDBText>
                        <Spacer size={SpaceType.Tiny} />
                        <VDBText
                          type={typography["Minor-M"]}
                          color={theme["core-05"]}
                        >
                          {stockItem.symmetry_short}
                        </VDBText>
                        <Spacer size={SpaceType.Small} />
                      </>
                    )}

                    {showfluor && (
                      <>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {t("requestHistoryDetail.fluor.text")}:
                        </VDBText>
                        <Spacer size={SpaceType.Tiny} />
                        <VDBText
                          type={typography["Minor-M"]}
                          color={theme["core-05"]}
                        >
                          {stockItem.fluor_intensity}
                        </VDBText>
                      </>
                    )}
                  </CPSContainer>
                )}
                <Spacer size={SpaceType.Small} />
                <StockContainer>
                  {showStockNumber && (
                    <CPSContainer>
                      <VDBText
                        type={typography["Minor-R"]}
                        color={theme["core-06"]}
                      >
                        {t("requestHistoryDetail.stockHash.text")}:
                      </VDBText>
                      <Spacer size={SpaceType.Small} />
                      <VDBText
                        type={typography["Minor-M"]}
                        color={theme["core-05"]}
                      >
                        {(!isCustomApp &&
                        stockItemRequest.bmh_request_type !==
                          "retail_buy_request"
                          ? "VDB-"
                          : "") + stockItem.stock_num}
                      </VDBText>
                    </CPSContainer>
                  )}
                </StockContainer>
                <StatusContainer>
                  <VDBText
                    type={typography["Minor-R"]}
                    color={
                      theme[
                        renderRequestStatus(
                          stockItemRequest?.memo_status
                            ? stockItemRequest?.request_notification_msg.toLowerCase()
                            : stockItemRequest?.status,
                          t
                        ).colorCode as keyof DefaultTheme
                      ] as string
                    }
                  >
                    {
                      renderRequestStatus(
                        stockItemRequest?.memo_status
                          ? stockItemRequest?.request_notification_msg.toLowerCase()
                          : stockItemRequest?.status,
                        t
                      ).status
                    }
                  </VDBText>
                </StatusContainer>
              </FlexContainer>
            </TopContainer>
          )}
          {isLinkedPair(stockItem) ? (
            <SecondSectionWithFilteredPairData
              listExpanded={true}
              mItem={stockItem}
              productType={productType?.productType}
              sectionName={SearchFilterTypeEnum.PAIRS}
              priceDetailsDataOptions={filteredPriceDetailsDataOptions}
              fromRequestPairs={true}
            />
          ) : isCustomApp ? (
            <SecondSectionWithFilteredData
              // listExpanded={showExpandedListView}
              mItem={stockItem}
              productType={productType?.productType}
              fromBulkList={true}
              priceDetailsDataOptions={filteredPriceDetailsDataOptions}
              editBulkItem={false}
              stoneType={stoneType}
              isLooseParcel={
                stockItem.diamond_category ===
                SearchFilterTypeEnumLowercase.LOOSE_PARCEL
              }
              sectionName={sectionName}
            />
          ) : (
            <>
              {[ProductCategory.GEMSTONE, ProductCategory.JEWELRY].includes(
                productType.productType
              ) && <LineViewContainer background={"core-03"} />}
              {[
                ProductCategory.DIAMOND,
                ProductCategory.LAB_GROWN_DIAMOND,
              ].includes(productType.productType) && (
                <ExtraDetailsContainer
                  stockItem={stockItem}
                  productType={productType}
                />
              )}
            </>
          )}
          {(hasVisibleConfig || hasVisiblePersonalization) && (
            <>
              {(isCustomApp ||
                isLinkedPair(stockItem) ||
                ![ProductCategory.GEMSTONE, ProductCategory.JEWELRY].includes(
                  productType.productType
                )) && <LineViewContainer background={"core-03"} />}
              {hasVisibleConfig && (
                <ItemDetails configurationDetails={visibleConfig} />
              )}
              {hasVisiblePersonalization && (
                <PersonalisationSection data={visiblePersonalization} />
              )}
              <LineViewContainer background={"core-03"} />
            </>
          )}
          {[
            RequestTypes.Buy,
            RequestTypes.Memo,
            RequestTypes.Hold,
            RequestTypes.Questions,
            RequestTypes.Internal,
            RequestTypes.RBR,
            RequestTypes.Quote,
          ].includes(requestType) &&
            !isTotalOfferPrice &&
            [PriceModes.WHITE, PriceModes.ORANGE].includes(
              userInfo.user.price_mode
            ) && (
              <BottomParentContainer
                allowQuantityOnBulk={
                  allowQuantityOnBulk &&
                  productType?.productType === ProductCategory.JEWELRY
                }
              >
                {allowQuantityOnBulk &&
                productType?.productType === ProductCategory.JEWELRY ? (
                  !isModifyScreen ? (
                    <>
                      <QuantityContainer>
                        <VDBText
                          color={theme["core-06"]}
                          type={typography["Body-R"]}
                        >
                          {t("orderCart.itemCard.quantityLabel.text")}
                        </VDBText>
                        <VDBText
                          color={theme["core-05"]}
                          type={typography["Body-M"]}
                        >
                          {stockItem?.quantity || 1}
                        </VDBText>
                      </QuantityContainer>
                    </>
                  ) : (
                    <QuantitySelector
                      min={1}
                      max={99}
                      label=""
                      onChange={(newQuantity: number) => {
                        saveBulkItemDetails?.(stockItem?.id, {
                          quantity: String(newQuantity),
                        });
                      }}
                      defaultValue={Number(stockItem?.quantity) || 1}
                    />
                  )
                ) : null}
                {stoneType === StoneType.PAIR ||
                stoneType === StoneType.PARCELS ||
                (isCustomApp &&
                  stockItem &&
                  productType?.productType &&
                  !(
                    stockItemRequest.request_type.includes("memo") ||
                    stockItemRequest.request_type.includes("hold") ||
                    stockItemRequest.request_type.includes("buy")
                  )) ? (
                  stockItem.diamond_category ===
                    SearchFilterTypeEnumLowercase.LOOSE_PARCEL &&
                  !isModifyScreen ? (
                    <BulkQuantityEditor
                      mItem={stockItem}
                      priceDetailsDataOptions={filteredPriceDetailsDataOptions}
                      viewOnly={true}
                    />
                  ) : stockItem.diamond_category !==
                    SearchFilterTypeEnumLowercase.LOOSE_PARCEL ? (
                    <PriceContainer
                      mItem={stockItem}
                      priceDetailsDataOptions={filteredPriceDetailsDataOptions}
                      fromBulkList={true}
                      isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
                      requestType={requestType}
                      viewType={stockItemRequest.view_type}
                    />
                  ) : (
                    <></>
                  )
                ) : (
                  <>
                    {stockItemRequest.offer_total_sales_price &&
                      stockItemRequest.offer_total_sales_price > 0 &&
                      !isListedEqualsOffer && (
                        <BottomContainer>
                          <BottonChildContainer screenWidth={keyCellWidth}>
                            {!isPurchasingAgent && isListedPriceDisplay && (
                              <VDBText
                                type={typography["Minor-R"]}
                                color={theme["core-05"]}
                              >
                                {t("requestHistoryDetail.listed.title.text")}
                              </VDBText>
                            )}
                          </BottonChildContainer>
                          <Spacer size={SpaceType.ExtraSmall} />
                          {validValue && isListedPriceDisplay && (
                            <BottonChildContainer screenWidth={keyCellWidth}>
                              <VDBText
                                type={typography["Minor-R"]}
                                color={theme["core-05"]}
                              >
                                {"Offer"}
                              </VDBText>
                            </BottonChildContainer>
                          )}
                        </BottomContainer>
                      )}
                    {[
                      ProductCategory.DIAMOND,
                      ProductCategory.LAB_GROWN_DIAMOND,
                      ProductCategory.GEMSTONE,
                    ].includes(productType.productType) ? (
                      <BottomContainer>
                        <BottonChildContainer screenWidth={cellWidth}>
                          <VDBText
                            type={typography["Minor-R"]}
                            color={theme["core-06"]}
                          >
                            {`${t(
                              "requestHistoryDetail.pricePerCarat.text"
                            )}: `}
                          </VDBText>
                          <Spacer size={SpaceType.ExtraSmall} />
                          {(isListedPriceDisplay || !isCustomApp) && (
                            <VDBText
                              type={typography["Minor-M"]}
                              color={theme["core-05"]}
                            >
                              {currency(stockItem.price_per_carat, {
                                symbol: stockItem.currency_symbol,
                                precision: 0,
                              }).format()}
                            </VDBText>
                          )}
                          <Spacer size={SpaceType.ExtraSmall} />
                        </BottonChildContainer>
                        {stockItemRequest.offer_total_sales_price &&
                          validValue &&
                          !isListedEqualsOffer &&
                          !stockItemRequest.request_type.includes("memo") && (
                            <BottonChildContainer screenWidth={cellWidth}>
                              <VDBText
                                type={typography["Minor-M"]}
                                color={theme["core-05"]}
                              >
                                {currency(
                                  stockItemRequest.offer_price_per_carat,
                                  {
                                    symbol: stockItem.currency_symbol,
                                    precision: 0,
                                  }
                                ).format()}
                              </VDBText>
                            </BottonChildContainer>
                          )}
                      </BottomContainer>
                    ) : null}
                    {[ProductCategory.DIAMOND].includes(
                      productType.productType
                    ) &&
                    [PriceModes.WHITE].includes(userInfo.user.price_mode) &&
                    !stockItem.is_fancy &&
                    stockItem.discount_percent ? (
                      <BottomContainer>
                        <BottonChildContainer screenWidth={cellWidth}>
                          <VDBText
                            type={typography["Minor-R"]}
                            color={theme["core-06"]}
                          >
                            {`${t(
                              "requestHistoryDetail.discountPercent.text"
                            )}: `}
                          </VDBText>
                          <Spacer size={SpaceType.ExtraSmall} />
                          {isListedPriceDisplay && (
                            <VDBText
                              type={typography["Minor-M"]}
                              color={theme["core-05"]}
                            >
                              {stockItem.discount_percent
                                ? percent(
                                    formatNumberWithPrecision(
                                      stockItem.discount_percent,
                                      { precision: 2 }
                                    )
                                  )
                                : "-"}
                            </VDBText>
                          )}
                        </BottonChildContainer>
                        <Spacer size={SpaceType.ExtraSmall} />
                        {stockItemRequest.offer_total_sales_price &&
                          validValue &&
                          !isListedEqualsOffer && (
                            <BottonChildContainer screenWidth={cellWidth}>
                              <VDBText
                                type={typography["Minor-M"]}
                                color={theme["core-05"]}
                              >
                                {stockItemRequest.offer_discount_percent
                                  ? percent(
                                      formatNumberWithPrecision(
                                        stockItemRequest.offer_discount_percent,
                                        { precision: 2 }
                                      )
                                    )
                                  : "-"}
                              </VDBText>
                            </BottonChildContainer>
                          )}
                      </BottomContainer>
                    ) : null}
                    <BottomContainer>
                      <BottonChildContainer screenWidth={cellWidth}>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {`${t(
                            "requestHistoryDetail.totalSalesPrice.text"
                          )}: `}
                        </VDBText>
                        <Spacer size={SpaceType.ExtraSmall} />
                        {(isListedPriceDisplay || !isCustomApp) && (
                          <VDBText
                            numberOfLines={1}
                            selectable={false}
                            type={typography["Minor-M"]}
                            color={theme["core-05"]}
                          >
                            {allowQuantityOnBulk &&
                            productType?.productType === ProductCategory.JEWELRY
                              ? currency(
                                  stockItem.total_sales_price *
                                    (stockItem.quantity || 1),
                                  {
                                    symbol: stockItem.currency_symbol,
                                    precision:
                                      userInfo?.roundUpPrices() &&
                                      !isMeleeItem(stockItem)
                                        ? 0
                                        : 2,
                                  }
                                ).format()
                              : currency(stockItem.total_sales_price, {
                                  symbol: stockItem.currency_symbol,
                                  precision:
                                    userInfo?.roundUpPrices() &&
                                    !isMeleeItem(stockItem)
                                      ? 0
                                      : 2,
                                }).format()}
                          </VDBText>
                        )}
                      </BottonChildContainer>
                      <Spacer size={SpaceType.ExtraSmall} />
                      {stockItemRequest.offer_total_sales_price &&
                      validValue &&
                      !isListedEqualsOffer &&
                      !stockItemRequest.request_type.includes("memo") ? (
                        <BottonChildContainer screenWidth={cellWidth}>
                          <VDBText
                            type={typography["Minor-M"]}
                            color={theme["core-05"]}
                          >
                            {currency(
                              stockItemRequest.offer_total_sales_price,
                              {
                                symbol: stockItem.currency_symbol,
                                precision:
                                  userInfo?.roundUpPrices() &&
                                  !isMeleeItem(stockItem)
                                    ? 0
                                    : 2,
                              }
                            ).format()}
                          </VDBText>
                        </BottonChildContainer>
                      ) : null}
                    </BottomContainer>
                  </>
                )}
              </BottomParentContainer>
            )}
          {commentToDisplay && hasChildren && (
            <CommentsContainer>
              <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                {getRequestType(stockItemRequest.request_type) === "Questions"
                  ? t("requestHistoryDetail.questions.text")
                  : t("requestHistoryDetail.comments.text")}
              </VDBText>
              <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
                {commentToDisplay}
              </VDBText>
            </CommentsContainer>
          )}
          {showRequestItemFromSupplier(matchingItem || stockItemRequest) &&
            (userInfo.getPriceMode() === PriceModes.WHITE ||
              (!isCustomApp &&
                userInfo?.getPriceMode() === PriceModes.ORANGE)) &&
            (isCustomApp ? useEnableRequestItemFromSupplier() : true) && (
              <RequestItemFromSupplierContainer
                onPress={() => {
                  if (isVDBOrg) {
                    handleSupplierForPaUser(
                      (matchingItem?.deep_link ||
                        stockItemRequest?.deep_link) ??
                        "",
                      navigate,
                      productPrefix
                    );
                  } else {
                    handleDeepLink(
                      (matchingItem?.deep_link ||
                        stockItemRequest?.deep_link) ??
                        ""
                    );
                  }
                }}
              >
                <VDBText
                  type={typography["Body-M"]}
                  color={theme["link-static"]}
                >
                  {t("requestHistory.requestItemFromSupplier.description.text")}
                </VDBText>
              </RequestItemFromSupplierContainer>
            )}
          {(stockItemRequest?.status === "decline" ||
            stockItemRequest?.status === "withdraw") &&
            hasChildren && (
              <ReasonContainer>
                <ReasonView>
                  <IconFrame>
                    <VDBIcon
                      color={theme["cont-00"]}
                      size={24}
                      variant={IconsVariant.VDBCustom}
                      name={VDBIcons.WARNING}
                    />
                  </IconFrame>
                  <View style={{ justifyContent: "space-between" }}>
                    <VDBText
                      type={typography["Minor-R"]}
                      color={theme["error-01"]}
                    >
                      {stockItemRequest?.status === "decline"
                        ? t("requestHistoryDetail.declineReason.title.text")
                        : t("requestHistoryDetail.withdrawReason.title.text")}
                    </VDBText>
                    <VDBText
                      type={typography["Minor-M"]}
                      color={theme["core-05"]}
                    >
                      {notesToDisplay ?? "-"}
                    </VDBText>
                  </View>
                </ReasonView>
              </ReasonContainer>
            )}
        </CardContainer>
      ) : (
        ""
      )}
    </>
  );
};

const TopWrapper = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const CardContainer = styled.Pressable`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-04"]};
`;

const TopContainer = styled.View`
  padding: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Basic}px;
  padding-right: ${SpaceType.Basic}px;
  flex-direction: row;
  align-items: center;
`;

const CPSContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const FlexContainer = styled.View`
  flex: 1;
`;

const StockContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BottomContainer = styled.View`
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  padding-vertical: ${SpaceType.Basic}px;
`;

const BottonChildContainer = styled.View<{
  screenWidth: number;
  justifyContent?: string;
}>`
  justify-content: flex-start;
  align-items: flex-end;
`;

const BottomParentContainer = styled.View<{
  allowQuantityOnBulk: boolean;
}>`
  flex-direction: row;
  justify-content: flex-end;
  padding-horizontal: ${SpaceType.Basic}px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  margin-right: 0.5px;
  align-items: flex-end;
  gap: ${SpaceType.Basic}px;
  border-color: ${({ theme }) => theme["core-03"]};
  ${({ allowQuantityOnBulk }) =>
    allowQuantityOnBulk &&
    css`
      justify-content: space-between;
      align-items: center;
    `}
`;

const CheckboxContainer = styled.View`
  margin-left: ${({ theme }) => (theme.isMobile ? `${SpaceType.Small}px` : 0)};
  align-items: center;
  justify-content: center;
`;

const CommentsContainer = styled.View`
  padding: ${SpaceType.Double}px;
  justify-content: flex-start;
  border-top-color: ${({ theme }) => theme["core-03"]};
  border-top-width: 1px;
  flex-direction: column;
  gap: ${SpaceType.ExtraSmall}px;
`;

const RequestItemFromSupplierContainer = styled.Pressable`
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  justify-content: center;
  align-items: center;
  border-top-color: ${({ theme }) => theme["core-04"]};
  border-top-width: 1px;
  flex-direction: column;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const ReasonContainer = styled.View`
  padding: ${SpaceType.Basic}px;
  justify-content: flex-start;
  border-top-color: ${({ theme }) => theme["core-03"]};
  border-top-width: 1px;
  flex-direction: column;
  gap: ${SpaceType.ExtraSmall}px;
  background-color: ${({ theme }) => theme["error-02"]};
  border-bottom-left-radius: ${SpaceType.Small}px;
  border-bottom-right-radius: ${SpaceType.Small}px;
`;

const IconContainer = styled.View`
  position: absolute;
  right: 0;
`;

const StatusContainer = styled.View`
  position: absolute;
  right: 0;
  bottom: 0;
`;

const PairStatusContainer = styled.View`
  position: absolute;
  right: 10;
  bottom: 8px;
`;

const IconFrame = styled.View<any>`
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: ${({ theme }) => theme["error-01"]};
  padding: 8px;
  margin-right: 8px;
  align-items: center;
`;

const ReasonView = styled.View<any>`
  flex-direction: row;
`;

const FirstContainer = styled.View<any>`
  height: 76px;
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-direction: row;
  justify-content: space-between;
`;

const QuantityContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.ExtraSmall}px;
`;

export default RequestViewCard;
